/**
 * Types and request helpers for the request-a-quote modal (`QuoteModal.tsx`): the props and line
 * types the modal shares with its renderers, the Prices API and delivery-times requests for the
 * modal's lines, the cut-processing mapper for the quote request, and the display helpers both
 * renderers use.
 *
 * Position: used by `QuoteModal.tsx`, `QuoteModalDesktop.tsx` and `QuoteModalMobile.tsx`.
 * `Catalog/CatalogItems.tsx` and `ShoppingCart/ShoppingCartTable/ShoppingCartTable.tsx` import
 * {@link QuoteInitialState} and {@link UpdatedProduct} to type what they pass the modal. The
 * request bodies come from `@ryerson/frontend.common` (`getRequestBodyForERPPricing`) and
 * `Helper/leadTimesHelpers.ts` (`getLeadTimeAndDeliveryDate`).
 *
 * Rules a change must keep:
 * - "the customer is never shown a price the Prices or Quotes API didn't return"; "the APIs may
 *   return a $0 value for price in which case we always show "TBD" to the end user and never
 *   "$0"". {@link getPricingInformationForItem} and {@link priceOrTbd} are where the modal turns
 *   a Prices API value into text: a 0 stays 0 and shows as TBD.
 * - "Before any response arrives, we should not be showing a price or TBD at all. It is still
 *   loading, so our typical standard would be an em-dash as a placeholder. Once the API loads or
 *   fails to load, we should be switching to TBD or the value returned."
 * - Each pricing and delivery-times line carries the quote line's `itemNumber`; the modal
 *   matches the responses back to its lines by SKU and that number.
 *
 * Today a missing price becomes `NaN` and shows as an em dash in three cases: before the pricing
 * response arrives, which matches the loading rule above; after a failed pricing call, when the
 * modal's `pricingData` stays `{}`; and for a line the response carried no price for. The last
 * two are known defects against the loading rule above.
 */
import React from "react";
import { InitialState } from "@components/Catalog/CatalogHelper";
import { getMappedProcessing } from "@components/Shared/ProcessingMapper";
import {
	DeliveryTimesRequestParams,
	getLeadTimeAndDeliveryDate,
} from "@components/Helper/leadTimesHelpers";
import {
	AjaxERPPricingResponse,
	cancelAllInFlightRequests,
	getCorrectQuantityAndWeightOfProduct,
	getCustomerPartNumber,
	getPricingOfProductsFromERP,
	getRequestBodyForERPPricing,
	Items,
	MatchingProduct,
	ProductSearchModel,
} from "@ryerson/frontend.common";
import { mapAddressTypesToPricingRequest } from "@ryerson/frontend.common";
import { ShoppingListProduct } from "@components/Helper/shoppingListHelper";
import { QuoteProcessing } from "@components/Models/CreateQuoteRequest.model";
import { Product } from "@ryerson/frontend.common";
import {
	displayPrice,
	emDash,
	Processing,
	ShoppingCartPricingItem,
	ShoppingCartPricingResponse,
	SalesPlantInformation,
} from "@ryerson/frontend.common";
import { Language, ShippingAndBillingInformation, ShipToAddress } from "@ryerson/frontend.common";
import { translateAttributes } from "@utils/translateAttributes";
import {
	Localization,
	UpdatedProductAttribute,
	AttributeLocalization,
} from "@components/Shared/ProductDisplay/Helpers/ProductDisplay.models";
import { AxiosInstance } from "axios";
import { QuoteModalContentProps } from "./QuoteModalContent";
import { ShoppingCartItem } from "@ryerson/frontend.e-commerce";
import { UOM } from "@ryerson/common.enums";
import { ProductsListState } from "@components/Models/GlobalSearchResults.Models";
import { PriceRequestResult } from "./QuoteModal";

/**
 * A parent's row state for one selected product, as the modal reads it: the catalog row
 * ({@link InitialState}) plus the reference, line PO (`purchaseOrderNumber`), notes and
 * "domestic requested" the parent already holds, which seed the quote line's inputs.
 */
export interface QuoteInitialState extends InitialState {
	customerRefNumber?: string;
	purchaseOrderNumber?: string;
	notes?: string;
	domesticRequired?: boolean;
	positionId: number;
}

/** A product with its Magenium id, used to link a line to its product details page. */
export interface UpdatedProduct extends Product {
	mageniumId: string;
}

/** Props of `QuoteModal.tsx` → `QuoteModal`, passed by the four pages that mount it. */
export type QuoteModalProps = {
	showQuoteModal: boolean;
	setShowQuoteModal: React.Dispatch<React.SetStateAction<boolean>>;
	/** The products to quote. Each is matched to its `productState` entry by SKU and `id`. */
	selectedProducts: Array<(UpdatedProduct | MatchingProduct) & { itemNumber: number }>;
	/** Not read by the modal. */
	clearSelectedProducts: () => void;
	/** Quantity, UOMs, cut and line inputs per selected product. */
	productState: (QuoteInitialState | ProductsListState)[];
	/** Not read by the modal. */
	bulkShoppingList?: ShoppingListProduct[];
	/**
	 * Called after a Save Quote success, right after the modal calls
	 * `setShowQuoteModal(false)`.
	 */
	onSuccess?: () => void;
	/**
	 * The calling page. Only the catalog passes it; the modal's one check on it (an `index`
	 * match for non-catalog callers) sits inside an arrow function that is never called.
	 */
	type?: "catalog" | "shoppingList" | "cart";
	/** Header PO for the quote; falls back to `profile.settings.purchaseOrderNumber`, then "". */
	purchaseOrderNumber?: string;
	/**
	 * Passed to `Helper/DeliveryDateAndMinMaxRange.ts` → `getDeliveryDateAndValidDateRange` when
	 * a line's requested date is chosen. Only the cart sets it.
	 */
	canSelectCustomerTBDPart?: boolean;
};

/**
 * One per-line input the renderers draw. `onChange` receives the change event for a text input
 * and the new `boolean` for the checkbox (`type: "checkbox"`).
 */
export type QuoteInput = {
	label: string;
	productTypeKey: QuoteModalProductTypeKeys;
	onChange: (event: any, product: QuoteModalProductType) => void;
	type?: "checkbox" | "text";
};

/**
 * Props `QuoteModal.tsx` passes to both renderers, `QuoteModalDesktop.tsx` and
 * `QuoteModalMobile.tsx`. `selectedProducts` is the modal's `displayItems` (lines not removed by
 * Remove & Reprice), and `quoteProductList` is every quote line, shown or not.
 * `handleSaveAsList` sends a Request Price (`handleOnClickSaveQuote(true)`); it saves no list.
 */
export type QuoteModalType = {
	quoteName: string;
	setQuoteName: (value: string) => void;
	selectedProducts: Array<Product | MatchingProduct>;
	showQuoteModal: boolean;
	setShowQuoteModal: React.Dispatch<React.SetStateAction<boolean>>;
	content: QuoteModalContentProps;
	quoteProductList: (QuoteModalProductType & { id: number | string })[];
	pricingData: ShoppingCartPricingResponse;
	handleOnClickSaveQuote: (priceRequest?: boolean) => void;
	handleSaveAsList: () => void;
	handleRemoveReprice: () => void;
	quoteInputs: QuoteInput[];
	submitted: boolean;
	quoteCreateSubmitted: boolean;
	/** Use this ref to bring invalid inputs into view when trying to submit. */
	validationInputRef?: React.MutableRefObject<HTMLElement | undefined>;
	priceRequestResult: PriceRequestResult;
	setPriceRequestResult: React.Dispatch<React.SetStateAction<PriceRequestResult>>;
	isRetrievingPricing: boolean;
};

/**
 * One quote line as the modal holds it, built from a selected product and its row state when
 * the modal opens (`QuoteModal.tsx`). The request takes price, UOMs and quantity from the
 * pricing response, not from here.
 */
export type QuoteModalProductType = {
	altPartNumber?: string;
	/** 1-based position of the product in `selectedProducts`; sent as the pricing line number. */
	itemNumber: number;
	materialSku: string;
	comboKey?: string;
	partNumber: string;
	/** Always "" in the modal; the line PO the customer types is `lineLevelPO`. */
	purchaseOrderNumber: string;
	quantity: number;
	quantityUom: UOM;
	/** Placeholder ("") at creation; not updated. */
	deliveryDate: string;
	/** Placeholder (0) at creation; not updated. */
	price: number;
	pricingUom: UOM;
	/** Placeholder ("0") at creation; not updated. */
	extendedPrice: string;
	domesticRequired: boolean;
	sourcePlant: string;
	lineInstructions: any[];
	/** The cut from the row state; `{}` when the row state has no `processing` key. */
	processing: Processing;
	customerRefNumber: string;
	lineItemNotes: string;
	lineLevelPO: string;
	/** Copied from the selected product's `positionId` when it has one, else 0; not sent. */
	positionId: number;
};

/** The quote-line fields the per-line inputs edit, keyed as in {@link QuoteModalProductType}. */
export enum QuoteModalProductTypeKeys {
	customerRefNumber = "customerRefNumber",
	domesticRequired = "domesticRequired",
	lineItemNotes = "lineItemNotes",
	lineLevelPO = "lineLevelPO",
}

/** The three text inputs of a quote line. Not referenced elsewhere in the repo. */
export type CustomerReferenceType = {
	customerRefNumber: string;
	lineItemNotes: string;
	lineLevelPO: string;
};

/**
 * Props of one desktop line (`QuoteModalDesktop.tsx` → `ProductRow`). `pricePerUom` and
 * `extendedPrice` come from {@link getPricingInformationForItem}: 0 means the Prices API returned
 * 0; `NaN` means no response yet, a failed pricing call, or no price for the line in the
 * response.
 */
export type ProductRowProps = {
	catalogProduct: UpdatedProduct;
	currentProductForQuote: QuoteModalProductType;
	content: QuoteModalContentProps;
	pricePerUom: number | undefined;
	extendedPrice: number | undefined;
	quoteInputs?: QuoteInput[];
	pricingUom: string;
	quantityUom: string;
	quantity: string | number;
	/** if the input is the first invalid in the list, attach the ref to it so we can scroll to it on submit*/
	attachValidationRef?: boolean;
	/** Use this ref to bring invalid inputs into view when trying to submit. */
	validationInputRef?: React.MutableRefObject<HTMLElement | undefined>;
};
/**
 * Props of the desktop footer (`QuoteModalDesktop.tsx` → `ButtonArea`).
 * `containsUncertainPricesOrInactiveItems` switches it from Save Quote to Request Price and
 * Remove & Reprice.
 */
export type ButtonAreaProps = {
	content: QuoteModalContentProps;
	handleOnClickSaveQuote: (priceRequest?: boolean) => void;
	pricingData: ShoppingCartPricingResponse;
	handleSaveAsList: () => void;
	handleRemoveReprice: () => void;
	selectedProducts: (Product | MatchingProduct)[];
	quoteCreateSubmitted: boolean;
	priceRequestResult: PriceRequestResult;
	containsUncertainPricesOrInactiveItems: boolean;
	isRetrievingPricing: boolean;
	showQuoteModal: boolean;
};

/**
 * Display text for a Prices API price: `tbd` for 0, the formatted price for any other number,
 * and an em dash for `undefined` or `NaN`. This is where a 0 price becomes "TBD" rather than
 * "$0". The em dash is the loading placeholder of the file header's loading rule; callers also
 * pass `NaN` after a failed pricing call and for a line with no price in the response, the
 * known defect the file header describes.
 *
 * The object-key idiom: each key is a condition's `toString()`, later keys overwrite earlier
 * ones, and the value under `"true"` is returned, so the last true condition wins.
 */
export const priceOrTbd = (price: number | undefined, tbd: string) =>
	({
		true: emDash,
		[(price === 0).toString()]: tbd,
		[(!!price).toString()]: displayPrice(Number(price)),
	})["true"];

/**
 * Prices the modal's shown lines through the Prices API (`@ryerson/frontend.common` →
 * `getPricingOfProductsFromERP`, which posts to `ApiUrl.PRICING_SERVICE_V2`). Sends one item per
 * quote line whose SKU is among `displayItems`, with `itemNumber` set to the line's number,
 * quantity and weight converted by `getCorrectQuantityAndWeightOfProduct`, and the line's cut.
 * Does nothing when the modal is closed or no line is shown.
 *
 * ZIP: a guest's `zipCode`; for a signed-in user the ship-to's 5-digit ZIP, else `zipCode`.
 * Requests still queued in `abortControllerRef` are aborted first. `handleOnSuccessPricing`
 * receives the response; `handleOnPricingFail` runs on an error. An aborted request calls
 * neither.
 */
export const getPricingForQuoteItems = (
	axiosInstance: AxiosInstance,
	isLoggedIn: boolean,
	showQuoteModal: boolean,
	customerPickup: boolean,
	zipCode: string,
	displayItems: Array<Product | MatchingProduct>,
	quoteProductList: QuoteModalProductType[],
	shipToAddress: ShipToAddress,
	shippingAndBillingInformation: ShippingAndBillingInformation,
	handleOnSuccessPricing: (response: AjaxERPPricingResponse) => void = () => {},
	customerNumber: string | null,
	shippingPlant: number | null,
	handleOnPricingFail?: () => void,
	abortControllerRef?: React.MutableRefObject<AbortController[]>
) => {
	if (showQuoteModal && displayItems.length > 0) {
		let pricingItemsRequestBody: Items[] = [];
		quoteProductList.forEach((item: QuoteModalProductType) => {
			let product = displayItems.find(
				(catalogProduct) => item.materialSku === catalogProduct.materialMaster
			);

			if (product) {
				const { updatedQuantity, updatedWeight } = getCorrectQuantityAndWeightOfProduct(
					product as unknown as ProductSearchModel,
					item.quantity,
					item.quantityUom,
					item.pricingUom
				);

				// TODO: this should be typed
				let requestBodyForPricingOfProduct = {
					materialSku: product.materialMaster,
					itemNumber: item.itemNumber,
					quantity: updatedQuantity,
					comboKey: product.comboKey || "",
					quantityUom: item.quantityUom,
					pricingUom: item.pricingUom,
					weight: Number(Number(updatedWeight).toFixed(3)),
					customerPartNumber: getCustomerPartNumber(product?.altPartNumber),
					...(item.processing &&
						Object.keys(item.processing).length > 0 && {
							processing: getMappedProcessing(item.processing),
						}),
				};
				pricingItemsRequestBody.push(requestBodyForPricingOfProduct);
			}
		});

		if (pricingItemsRequestBody.length > 0) {
			let requestBodyOfPricing = getRequestBodyForERPPricing({
				shippingPlant,
				zipCode: !isLoggedIn ? zipCode : shipToAddress?.zipCode?.substring(0, 5) || zipCode,
				items: pricingItemsRequestBody,
				customerPickup,
				address: mapAddressTypesToPricingRequest(
					isLoggedIn,
					shipToAddress,
					shippingAndBillingInformation,
					customerNumber,
					customerPickup
				),
			});

			abortControllerRef?.current.length &&
				cancelAllInFlightRequests(abortControllerRef.current);

			getPricingOfProductsFromERP(
				axiosInstance,
				requestBodyOfPricing,
				handleOnSuccessPricing,
				handleOnPricingFail,
				abortControllerRef?.current
			);
		}
	}
};

/**
 * Fetches delivery dates for the modal's shown lines through `Helper/leadTimesHelpers.ts` →
 * `getLeadTimeAndDeliveryDate`. One request line per quote line whose SKU is among
 * `displayItems`, with `id` set to the line's `itemNumber` so the response lines match back.
 * Does nothing when the modal is closed or no line is shown. A failed request is ignored: the
 * error callback is a no-op, and the quote request then sends lines without a date.
 */
export const getDeliveryDateForQuoteItems = (
	axiosInstance: AxiosInstance,
	isLoggedIn: boolean,
	showQuoteModal: boolean,
	customerPickup: boolean,
	zipCode: string,
	displayItems: Array<Product | MatchingProduct>,
	quoteProductList: QuoteModalProductType[],
	shipToAddress: ShipToAddress,
	salesPlant: SalesPlantInformation,
	handleOnSuccessDeliveryDateAndLeadTime: (response: any) => void = () => {},
	defaultShipToAddress: ShipToAddress,
	abortControllerRef?: React.MutableRefObject<AbortController[]>
) => {
	if (showQuoteModal && displayItems.length > 0) {
		let leadTimeRequestBody: DeliveryTimesRequestParams[] = [];
		quoteProductList.forEach((item: QuoteModalProductType) => {
			let product = displayItems.find(
				(catalogProduct) => item.materialSku === catalogProduct.materialMaster
			);
			if (product) {
				let requestBodyForLeadTime = {
					id: item.itemNumber,
					materialMaster: item?.materialSku ?? "",
					qty: item?.quantity ?? "",
					comboKey: item.comboKey ?? "",
					qtyUOM: item?.quantityUom ?? "",
					priceUOM: item?.pricingUom ?? "",
					customerPartNo: "",
					...(item.processing &&
						Object.keys(item.processing).length > 0 && {
							processing: getMappedProcessing(item.processing),
						}),
				};
				leadTimeRequestBody.push(requestBodyForLeadTime);
			}
		});
		getLeadTimeAndDeliveryDate(
			isLoggedIn,
			axiosInstance,
			zipCode,
			customerPickup,
			shipToAddress,
			defaultShipToAddress,
			salesPlant,
			leadTimeRequestBody,
			handleOnSuccessDeliveryDateAndLeadTime,
			() => {},
			abortControllerRef?.current
		);
	}
};

/**
 * Sets the shown lines to `products` minus every product whose SKU matches an omitted line.
 * Matching is by SKU only, so omitting one line of a SKU hides all its lines.
 */
export const updateDisplayItems = (
	products: Array<(Product | MatchingProduct) & { itemNumber: string | number }>,
	omittedItems: QuoteModalProductType[],
	setDisplayItems: React.Dispatch<
		React.SetStateAction<
			Array<
				(Product | MatchingProduct) & {
					itemNumber: string | number;
				}
			>
		>
	>
) => {
	const findItems = products.filter(
		(product) => !omittedItems.some((omit) => omit.materialSku === product.materialMaster)
	);
	setDisplayItems([...findItems]);
};

/**
 * Maps a line's cut (`Processing` from `@ryerson/frontend.common`) to the Quotes API
 * `cutProcessing` shape (`Models/CreateQuoteRequest.model.tsx` → `QuoteProcessing`). Dimensions
 * and tolerances become numbers, with 0 for a missing or non-numeric value; the tolerance keys
 * are renamed.
 */
export const mapProcessingToRequestFormat = (processing: Processing): QuoteProcessing => ({
	finish: processing?.finish,
	insideDiameter: Number(processing?.insideDiameter) || 0,
	length: Number(processing?.length) || 0,
	maxTolerance: Number(processing?.toleranceMax) || 0,
	maxToleranceInnerDiameter: Number(processing?.toleranceMaxInsideDiameter) || 0,
	minTolerance: Number(processing?.toleranceMin) || 0,
	minToleranceInnerDiameter: Number(processing?.toleranceMinInsideDiameter) || 0,
	outsideDiameter: Number(processing?.outsideDiameter) || 0,
	process: processing?.process,
	shape: processing?.shape,
	width: Number(processing?.width) || 0,
});

/**
 * Finds a line's entry in the Prices API response, matched by SKU and `itemNumber === index + 1`
 * where `index` is the line's position in `quoteProductList`. `pricePerUom` is 0 when the API
 * returned 0, and `NaN` when the entry has no price or there is no entry (no response yet, a
 * failed call, or no match); the renderers show these as "TBD" and an em dash through
 * {@link priceOrTbd} (see the file header for where the em dash is a known defect).
 * `itemPricingData` is `{}` when no entry matched. `currentProductForQuote` is not read.
 */
export const getPricingInformationForItem = (
	index: number,
	currentProductForQuote: QuoteModalProductType,
	pricingData: ShoppingCartPricingResponse,
	currentProduct: Product | MatchingProduct
) => {
	let itemPricingData =
		pricingData?.items?.find((item: ShoppingCartPricingItem) => {
			return (
				item.materialSku === currentProduct?.materialMaster && item.itemNumber === index + 1
			);
		}) || ({} as ShoppingCartPricingItem);
	const pricePerUom = {
		true: Number(itemPricingData.pricePerUom),
		[(itemPricingData.pricePerUom === 0).toString()]: 0,
		[(!itemPricingData.pricePerUom && itemPricingData.pricePerUom !== 0).toString()]: NaN,
	}["true"];

	const extendedPrice = Number(itemPricingData.extendedPrice);

	return {
		pricePerUom: pricePerUom,
		extendedPrice: extendedPrice,
		itemPricingData: itemPricingData,
	};
};

/**
 * Reshapes a quote line for `Helper/shoppingCartHelper.ts` → `hasErpInvalidInputs` and
 * `findInvalidErpInputIndex`, which check `customerRefNumber`, `purchaseOrderNumber` and
 * `notes`. The modal's line PO is `lineLevelPO`, not `purchaseOrderNumber` (always ""), so those
 * checks do not cover it; `QuoteModal.tsx` → `invalidQuoteInputs` does.
 *
 * Always returns an object, so as a `findIndex` predicate (as both renderers use it) it matches
 * the first element.
 */
export const intoErpValidator = (
	item: Pick<QuoteModalProductType, "customerRefNumber" | "purchaseOrderNumber" | "lineItemNotes">
): Pick<ShoppingCartItem, "customerRefNumber" | "purchaseOrderNumber" | "notes"> => {
	return { ...item, notes: item.lineItemNotes }; // type mapping
};

/** The availability flags {@link checkIsActiveItem} reads from a product. */
export interface HasActiveAvailableProps {
	available?: boolean;
	active?: boolean;
}

/**
 * Name and value of one product attribute in the given language, for the "Cut From" detail of
 * a line with a cut. The value is an em dash when the attribute has no value or no
 * localization.
 */
export const resolveAttributeDisplay = (
	attribute: UpdatedProductAttribute,
	language: Language,
	local: keyof AttributeLocalization
): { name: string; value: string } => ({
	name: translateAttributes(attribute?.localization?.[local]?.name || "", language),
	value:
		attribute?.value && attribute?.localization
			? attribute?.localization?.[local]?.value
			: emDash,
});

/**
 * Check if an item is active and available. If the item has both properties, both must be
 * true; with only `active`, that decides; with neither (or no item), it is inactive. Used by
 * the quote modal only. The shopping-list price view has its own stricter copy
 * (`ShoppingListDetails/Helpers/ViewPrice/ViewPriceCommon.tsx` → `checkIsActiveItem`), which
 * always requires both.
 */
export const checkIsActiveItem = <T extends HasActiveAvailableProps>(item: T) => {
	if (typeof item?.active !== "undefined" && typeof item?.available !== "undefined") {
		return !!item?.available && !!item?.active;
	}

	return !!item?.active;
};
